import React from "react";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { postApi, putApi } from "../../services/apiServices";
import { toast } from "react-toastify";

function Settings() {
  const [activeSection, setActiveSection] = useState('profile');
  const [changePasswordData, setChangePasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_new_password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    new_password: false,
    confirm_new_password: false
  });
  const [profileData, setProfileData] = useState({
    email: '',
    phone_number: '',
    full_name: '',
    role: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    new_password: [],
    confirm_new_password: []
  });

  // Fetch user details from localStorage on component mount
  useEffect(() => {
    const userDetails = localStorage.getItem('userDetail');
    if (userDetails) {
      try {
        const userData = JSON.parse(userDetails);
        setProfileData({
          email: userData.email || '',
          phone_number: userData.phone_number || '',
          full_name: userData.full_name || '',
          role: userData.role || ''
        });
        if (userData.selfie_image) {
          setProfileImagePreview(userData.selfie_image);
        }
      } catch (error) {
        console.error('Error parsing user details:', error);
      }
    }
  }, []);

  const handleSectionClick = (section) => {
    setActiveSection(section);
    setMessage('');
  };

  const handleChangePasswordInput = (e) => {
    const { name, value } = e.target;
    setChangePasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation for new password
    if (name === 'new_password') {
      const errors = validatePassword(value);
      setPasswordErrors(prev => ({
        ...prev,
        new_password: errors
      }));
    }

    // Real-time validation for confirm password
    if (name === 'confirm_new_password') {
      const errors = [];
      if (!value) {
        errors.push('Confirm password is required');
      } else if (value.length < 8) {
        errors.push('Password must be at least 8 characters long');
      } else if (value !== changePasswordData.new_password) {
        errors.push('Passwords do not match');
      }
      setPasswordErrors(prev => ({
        ...prev,
        confirm_new_password: errors
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (!password) {
      errors.push('Password is required');
      return errors;
    }
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    return errors;
  };

  const handleProfileInput = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    // Simple validation
    if (!changePasswordData.old_password) {
      toast.error('Current password is required');
      setIsLoading(false);
      return;
    }

    const newPasswordErrors = validatePassword(changePasswordData.new_password);
    if (newPasswordErrors.length > 0) {
      toast.error(newPasswordErrors[0]); // Show first error
      setIsLoading(false);
      return;
    }

    if (changePasswordData.new_password !== changePasswordData.confirm_new_password) {
      toast.error('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await postApi('/change-password', changePasswordData);
      toast.success(response.message || 'Password changed successfully!');
      setChangePasswordData({
        old_password: '',
        new_password: '',
        confirm_new_password: ''
      });
      setPasswordErrors({
        new_password: [],
        confirm_new_password: []
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setIsProfileLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      
      // Add email if provided
      if (profileData.email) {
        formData.append('email', profileData.email);
      }
      
      // Add phone number if provided
      if (profileData.phone_number) {
        formData.append('phone_number', profileData.phone_number);
      }
      
      // Add image if provided
      if (profileImage) {
        formData.append('image', profileImage);
      }

      const response = await putApi('/update-profile', formData);
      setMessage('Profile updated successfully!');
      
      // Update localStorage with new data
      const userDetails = localStorage.getItem('userDetail');
      if (userDetails) {
        const userData = JSON.parse(userDetails);
        const updatedUserData = {
          ...userData,
          email: profileData.email || userData.email,
          phone_number: profileData.phone_number || userData.phone_number,
          selfie_image: profileImagePreview || userData.selfie_image
        };
        localStorage.setItem('userDetail', JSON.stringify(updatedUserData));
      }
      
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsProfileLoading(false);
    }
  };

  return (
    <>
      <div className="right-section">
        <div className="left-side">
          <ul>
            <li className={activeSection === 'profile' ? 'active' : ''}>
              <span onClick={() => handleSectionClick('profile')}>
                <a href="#" onClick={(e) => e.preventDefault()}>Profile</a>
              </span>
              <img src="/images/right-icon.svg" alt="logo" />
            </li>
            <li className={activeSection === 'change-password' ? 'active' : ''}>
              <span onClick={() => handleSectionClick('change-password')}>
                <a href="#" onClick={(e) => e.preventDefault()}>Change Password</a>
              </span>
              <img src="/images/right-icon.svg" alt="logo" />
            </li>
         
          </ul>
        </div>
        <div className="right-side">
          {activeSection === 'profile' ? (
            <div className="seeting-profile">
              <form onSubmit={handleProfileSubmit}>
                <div className="profile-header text-center position-relative">
                  <div className="profile-image-container">
                    {profileImagePreview ? (
                      <img src={profileImagePreview} className="profile-image" alt="Profile" />
                    ) : (
                      <img src="/images/profile-ellips.svg" className="profile-image" alt="Profile" />
                    )}
                    <input
                      type="file"
                      id="profile-image-upload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="profile-image-upload" className="btn profile-edit-btn">
                      <img alt="Edit" src="/images/edit-icon.svg" />
                    </label>
                  </div>
                </div>
                
                <div className="row mt-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter your name"
                        name="full_name"
                        value={profileData.full_name}
                        onChange={handleProfileInput}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Enter your email"
                        name="email"
                        value={profileData.email}
                        onChange={handleProfileInput}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="Enter your phone number"
                        name="phone_number"
                        value={profileData.phone_number}
                        onChange={handleProfileInput}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Role</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Your role"
                        name="role"
                        value={profileData.role}
                        onChange={handleProfileInput}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group">
                      <button 
                        type="submit" 
                        className="btn btn-upload-profile"
                        disabled={isProfileLoading}
                      >
                        {isProfileLoading ? 'Updating Profile...' : 'Update Profile'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          ) : (
            <div className="change-password-section">
              <div className="seeting-profile">
              <div className="password-header text-center">
                <h4>Change Password</h4>
              </div>
              <form onSubmit={handleChangePasswordSubmit}>
                <div className="row mt-3">
                  <div className="col-md-6">
                    <div className="form-group position-relative">
                      <label>Current Password</label>
                      <input
                        type={showPasswords.old_password ? "text" : "password"}
                        className="form-control"
                        placeholder="Enter your current password"
                        name="old_password"
                        value={changePasswordData.old_password}
                        onChange={handleChangePasswordInput}
                        required
                      />
                      <span 
                        className="pass-show" 
                        onClick={() => togglePasswordVisibility('old_password')} 
                        style={{ cursor: 'pointer' }}
                      >
                        {showPasswords.old_password ? (
                          <img src="/images/view.svg" alt="Hide password" />
                        ) : (
                          <img src="/images/show-pass.svg" alt="Show password" />
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group position-relative">
                      <label>New Password</label>
                      <input
                        type={showPasswords.new_password ? "text" : "password"}
                        className={`form-control ${passwordErrors.new_password.length > 0 ? 'is-invalid' : ''}`}
                        placeholder="Enter your new password"
                        name="new_password"
                        value={changePasswordData.new_password}
                        onChange={handleChangePasswordInput}
                        required
                      />
                      <span 
                        className="pass-show" 
                        onClick={() => togglePasswordVisibility('new_password')} 
                        style={{ cursor: 'pointer' }}
                      >
                        {showPasswords.new_password ? (
                          <img src="/images/view.svg" alt="Hide password" />
                        ) : (
                          <img src="/images/show-pass.svg" alt="Show password" />
                        )}
                      </span>
                      {passwordErrors.new_password.length > 0 && (
                        <div className="invalid-feedback d-block">
                          <ul className="mb-0">
                            {passwordErrors.new_password.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group position-relative">
                      <label>Confirm New Password</label>
                      <input
                        type={showPasswords.confirm_new_password ? "text" : "password"}
                        className={`form-control ${passwordErrors.confirm_new_password.length > 0 ? 'is-invalid' : ''}`}
                        placeholder="Confirm your new password"
                        name="confirm_new_password"
                        value={changePasswordData.confirm_new_password}
                        onChange={handleChangePasswordInput}
                        required
                      />
                      <span 
                        className="pass-show" 
                        onClick={() => togglePasswordVisibility('confirm_new_password')} 
                        style={{ cursor: 'pointer' }}
                      >
                        {showPasswords.confirm_new_password ? (
                          <img src="/images/view.svg" alt="Hide password" />
                        ) : (
                          <img src="/images/show-pass.svg" alt="Show password" />
                        )}
                      </span>
                      {passwordErrors.confirm_new_password.length > 0 && (
                        <div className="invalid-feedback d-block">
                          <ul className="mb-0">
                            {passwordErrors.confirm_new_password.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form-group">
                      <button 
                        type="submit" 
                        className="btn change-password"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Changing Password...' : 'Change Password'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Settings;
